import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { ProductLockup } from '../components/brand/Brand'
import { getSupabaseBrowserClient } from '../lib/supabase'
import { useAuth } from './AuthProvider'

function safeReturnTo(value: string | null) {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return '/'
  return value
}

function SupportAuthFrame({
  eyebrow,
  title,
  description,
  children,
}: {
  eyebrow: string
  title: string
  description: string
  children: ReactNode
}) {
  return (
    <main className="support-auth-page">
      <section className="support-auth-brand">
        <ProductLockup inverse />
        <div>
          <p className="eyebrow">Swell AI</p>
          <h1>Your organisation’s AI governance framework, in one place.</h1>
          <p>Return to the same governed view of AI use, ownership, strategy and attention once your account is ready.</p>
        </div>
      </section>
      <section className="support-auth-form" aria-labelledby="support-auth-title">
        <div className="support-auth-card">
          <p className="eyebrow">{eyebrow}</p>
          <h2 id="support-auth-title">{title}</h2>
          <p className="muted">{description}</p>
          {children}
        </div>
      </section>
    </main>
  )
}


export function AuthEmailConfirmationPage() {
  const [searchParams] = useSearchParams()
  const tokenHash = searchParams.get('token_hash')
  const rawConfirmationType = searchParams.get('type')
  const confirmationType: 'email' | 'invite' | 'magiclink' = rawConfirmationType === 'invite'
    ? 'invite'
    : rawConfirmationType === 'magiclink'
      ? 'magiclink'
      : 'email'
  const next = safeReturnTo(searchParams.get('next'))
  const [verifying, setVerifying] = useState(false)
  const [verificationError, setVerificationError] = useState<string | null>(null)

  useEffect(() => {
    if (!tokenHash) {
      window.location.replace('/auth/login?notice=access-link-unavailable')
    }
  }, [tokenHash])

  const continueSecurely = async () => {
    if (!tokenHash || verifying) return
    setVerifying(true)
    setVerificationError(null)

    const { error } = await getSupabaseBrowserClient().auth.verifyOtp({
      token_hash: tokenHash,
      type: confirmationType,
    })

    if (error) {
      // Used, expired and malformed one-time links all fail without revealing
      // whether an account or task exists.
      setVerificationError('This secure access link is unavailable. Sign in or request a new password link to continue.')
      setVerifying(false)
      return
    }

    const destination = confirmationType === 'invite'
      ? `/auth/update-password?firstAccess=1&returnTo=${encodeURIComponent(next)}`
      : next
    window.location.replace(destination)
  }

  return (
    <SupportAuthFrame eyebrow="Secure access" title="Continue securely" description="Confirm that you want to sign in and open the task prepared for you.">
      {verificationError ? (
        <div className="form-stack">
          <div className="inline-alert danger" role="alert">{verificationError}</div>
          <Link className="button primary" to={'/auth/login?returnTo=' + encodeURIComponent(next)}>Go to sign in</Link>
        </div>
      ) : (
        <button className="button primary" type="button" onClick={() => { void continueSecurely() }} disabled={verifying || !tokenHash}>
          {verifying ? 'Confirming secure access…' : 'Continue securely'}
        </button>
      )}
    </SupportAuthFrame>
  )
}
export function ForgotPasswordExperiencePage() {
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setSubmitting(true)
    const supabase = getSupabaseBrowserClient()
    await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo: `${window.location.origin}/auth/update-password` })
    setSubmitting(false)
    setMessage('If that address belongs to a Swell AI account, a password reset email is on its way.')
  }

  return (
    <SupportAuthFrame eyebrow="Account recovery" title="Reset your password" description="Enter your account email and we will send a secure recovery link if it matches a Swell AI account.">
      <form className="form-stack" onSubmit={(event) => { void submit(event) }}>
        <label><span>Email</span><input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required autoFocus /></label>
        {message ? <div className="inline-alert success">{message}</div> : null}
        <button className="button primary" type="submit" disabled={submitting}>{submitting ? 'Sending…' : 'Send recovery link'}</button>
      </form>
      <div className="auth-links"><Link to="/auth/login">Back to sign in</Link></div>
    </SupportAuthFrame>
  )
}

export function UpdatePasswordExperiencePage() {
  const { session, refreshIdentity } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const firstAccess = searchParams.get('firstAccess') === '1'
  const returnTo = safeReturnTo(searchParams.get('returnTo'))
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    if (password.length < 12) return setError('Use at least 12 characters for your password.')
    if (password !== confirmPassword) return setError('The passwords do not match.')

    setSubmitting(true)
    const supabase = getSupabaseBrowserClient()
    const { error: updateError } = await supabase.auth.updateUser({ password })
    if (updateError) {
      setSubmitting(false)
      return setError(updateError.message)
    }

    try {
      await refreshIdentity()

      if (firstAccess) {
        // Password setup is already complete at this point. Welcome-email
        // delivery must never block the user from entering Swell; the trusted
        // outbox retains retry state if the immediate dispatch is unavailable.
        await supabase.functions.invoke('send-account-welcome').catch(() => undefined)
      }

      void navigate(returnTo, { replace: true })
    } catch (completionError) {
      setError(completionError instanceof Error ? completionError.message : 'Your password was saved, but account setup could not be completed.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <SupportAuthFrame
      eyebrow="Secure account"
      title={firstAccess ? 'Create your password' : 'Choose a new password'}
      description={firstAccess ? 'Set a unique password of at least 12 characters before continuing to Swell AI.' : 'Use a unique password of at least 12 characters.'}
    >
      {!session ? (
        <div className="inline-alert neutral">Open the recovery or secure access link from your email to continue.</div>
      ) : (
        <form className="form-stack" onSubmit={(event) => { void submit(event) }}>
          <label><span>New password</span><input type="password" autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} minLength={12} required autoFocus /></label>
          <label><span>Confirm password</span><input type="password" autoComplete="new-password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} minLength={12} required /></label>
          {error ? <div className="inline-alert danger" role="alert">{error}</div> : null}
          <button className="button primary" type="submit" disabled={submitting}>{submitting ? 'Saving…' : firstAccess ? 'Continue to Swell AI' : 'Save password'}</button>
        </form>
      )}
    </SupportAuthFrame>
  )
}

export function InviteAcceptanceExperiencePage() {
  const { session } = useAuth()
  const [searchParams] = useSearchParams()
  const returnTo = safeReturnTo(searchParams.get('returnTo'))

  if (!session) {
    return (
      <SupportAuthFrame eyebrow="Organisation invitation" title="Open your invitation link" description="Use the invitation link from your email so Swell can confirm the organisation you were invited to join.">
        <div className="inline-alert neutral">Already have a Swell AI account? Sign in with the same verified email and your pending invitation can be applied to that account.</div>
        <Link className="button secondary" to={'/auth/login?returnTo=' + encodeURIComponent(`/invite/accept?returnTo=${returnTo}`)}>Sign in</Link>
      </SupportAuthFrame>
    )
  }

  return <Navigate to={returnTo} replace />
}
